import React, { useState } from 'react';
import { FiChevronDown, FiChevronUp } from 'react-icons/fi';
import { useI18n } from '../../../i18n';
import type { BreakSlot, DutySlot } from '../../../services/dutyBoardService';
import { formatAbsoluteMinute } from '../../../utils/siteScheduling';
import { formatMoney, triggerDetail, triggerLabel } from '../../analytics/profitabilityPresentation';
import { usePayFlags } from './usePayFlags';
import './PayFlagsCallout.css';

interface PayFlagsCalloutProps {
  siteId: string | null;
  slotDate: string;
  slots: DutySlot[];
  breakSlots: BreakSlot[];
  canSeePay: boolean;
  /** True while the day can still be changed, which is when the flags are a decision rather than a record. */
  isDraft: boolean;
}

/**
 * A strip under the publication bar naming the shifts on this day that trigger
 * a premium or a minimum payment. It informs and never blocks: paying a
 * premium is lawful, the planner should just know before publishing.
 */
export default function PayFlagsCallout({
  siteId,
  slotDate,
  slots,
  breakSlots,
  canSeePay,
  isDraft,
}: PayFlagsCalloutProps) {
  const { t, tn } = useI18n();
  const [isOpen, setIsOpen] = useState(false);
  const { summary, currencyCode, showAmounts } = usePayFlags({ siteId, slotDate, slots, breakSlots, canSeePay });

  if (!summary || summary.shifts.length === 0) return null;

  const count = summary.shifts.length;

  return (
    <>
      <div className="db-callout db-callout--pay" role="status">
        <span className="db-callout__label">{t('Pay flags')}</span>
        <span className="db-callout__copy">
          {isDraft
            ? tn(
              '{count} shift will cost more than its hours if published as it stands.',
              '{count} shifts will cost more than their hours if published as they stand.',
              count,
            )
            : tn(
              '{count} shift on this roster costs more than its hours.',
              '{count} shifts on this roster cost more than their hours.',
              count,
            )}
          {showAmounts && summary.triggerAmount > 0 && (
            <> {t('That adds {amount} to a day of {total}.', {
              amount: formatMoney(summary.triggerAmount, currencyCode),
              total: formatMoney(summary.grossPay, currencyCode),
            })}</>
          )}
        </span>
        <span className="db-payflags__chips">
          {summary.totals.map((total) => (
            <span key={total.kind} className="db-payflags__chip">
              {triggerLabel(total.kind, t)}
              <strong>×{total.shiftCount}</strong>
            </span>
          ))}
        </span>
        <button
          type="button"
          className="db-callout__action"
          onClick={() => setIsOpen((open) => !open)}
          aria-expanded={isOpen}
          aria-controls="db-payflags-list"
        >
          {isOpen ? <FiChevronUp aria-hidden="true" /> : <FiChevronDown aria-hidden="true" />}
          {isOpen ? t('Hide shifts') : t('Show shifts')}
        </button>
      </div>

      {isOpen && (
        <ul className="db-payflags" id="db-payflags-list">
          {summary.shifts.map((entry) => (
            <li key={entry.shift.id} className="db-payflags__shift">
              <span className="db-payflags__who">
                <strong>{entry.shift.guardName}</strong>
                <span>
                  {formatAbsoluteMinute(entry.shift.startMinute)} - {formatAbsoluteMinute(entry.shift.endMinute)}
                </span>
              </span>
              <span className="db-payflags__why">
                {entry.triggers.map((trigger) => (
                  <span key={trigger.kind} className="db-payflags__trigger">
                    <strong>{triggerLabel(trigger.kind, t)}</strong>
                    <span>{triggerDetail(trigger, t)}</span>
                    {showAmounts && trigger.amount > 0 && (
                      <em>+{formatMoney(trigger.amount, currencyCode)}</em>
                    )}
                  </span>
                ))}
              </span>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
